import { useEffect, useState, type CSSProperties } from "react";
import useVersionCheck from "../hooks/useVersionCheck";

function resolveIsDark(): boolean {
  if (typeof window === "undefined") return false;
  const classList = document.documentElement.classList;
  if (classList.contains("dark")) return true;
  if (classList.contains("light")) return false;
  return typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-color-scheme: dark)").matches
    : false;
}

function useIsDark(): boolean {
  const [isDark, setIsDark] = useState<boolean>(() => resolveIsDark());

  useEffect(() => {
    const update = () => setIsDark(resolveIsDark());

    const mql =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-color-scheme: dark)")
        : null;
    mql?.addEventListener("change", update);

    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      mql?.removeEventListener("change", update);
      observer.disconnect();
    };
  }, []);

  return isDark;
}

const lightPalette = {
  bg: "#ffffff",
  fg: "#111827",
  border: "#e5e7eb",
  muted: "#6b7280",
  mutedHover: "#111827",
  primary: "#2563eb",
  primaryHover: "#1d4ed8",
  primaryFg: "#ffffff",
  shadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
};

const darkPalette = {
  bg: "#1f2937",
  fg: "#f3f4f6",
  border: "#374151",
  muted: "#9ca3af",
  mutedHover: "#f3f4f6",
  primary: "#3b82f6",
  primaryHover: "#60a5fa",
  primaryFg: "#0b1220",
  shadow: "0 4px 12px rgba(0, 0, 0, 0.35)",
};

export default function UpdateAvailableToast() {
  const { updateAvailable, refresh } = useVersionCheck();
  const [dismissed, setDismissed] = useState(false);
  const isDark = useIsDark();

  if (!updateAvailable || dismissed) return null;

  const palette = isDark ? darkPalette : lightPalette;

  const containerStyle: CSSProperties = {
    position: "fixed",
    top: 16,
    right: 16,
    maxWidth: 380,
    padding: "10px 12px",
    borderRadius: 8,
    backgroundColor: palette.bg,
    color: palette.fg,
    border: `1px solid ${palette.border}`,
    boxShadow: palette.shadow,
    fontSize: 13,
    lineHeight: 1.4,
    display: "flex",
    alignItems: "center",
    gap: 10,
    zIndex: 9999,
  };

  const messageStyle: CSSProperties = {
    flex: 1,
    margin: 0,
  };

  const actionsStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  };

  const refreshButtonStyle: CSSProperties = {
    padding: "4px 10px",
    borderRadius: 6,
    border: "none",
    backgroundColor: palette.primary,
    color: palette.primaryFg,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  };

  const dismissButtonStyle: CSSProperties = {
    padding: "2px 6px",
    borderRadius: 4,
    border: "none",
    backgroundColor: "transparent",
    color: palette.muted,
    fontSize: 16,
    lineHeight: 1,
    cursor: "pointer",
  };

  return (
    <div role="status" aria-live="polite" style={containerStyle}>
      <p style={messageStyle}>
        Uma nova versão do T2 Educação está disponível.
      </p>
      <div style={actionsStyle}>
        <button
          type="button"
          onClick={refresh}
          style={refreshButtonStyle}
          aria-label="Atualizar"
        >
          Atualizar
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          style={dismissButtonStyle}
          aria-label="Fechar"
        >
          ×
        </button>
      </div>
    </div>
  );
}
